import type { ThemeColorProperties } from "@/types/theme";
import { defaultTheme } from "@/theme/theme";

/**
 * Body copy and headings stay on a fixed dark neutral (prototype #0d2033).
 * FI theme drives accent, link, label, border, and KPI colors via the eight
 * ThemeColorProperties fields only (see institutionThemeToCssVars).
 */
export const CONTENT_FOREGROUND_FIXED = "#0d2033";

export type ThemeBorderWeight = "default" | "input";

/** Faint content border from sidebar backgroundColor + neutralColor (API fields only). */
export const borderFromSidebar = (
  backgroundColor: string,
  neutralColor: string,
  weight: ThemeBorderWeight,
): string =>
  weight === "default"
    ? `color-mix(in srgb, ${backgroundColor} 22%, ${neutralColor})`
    : `color-mix(in srgb, ${backgroundColor} 30%, ${neutralColor})`;

/** Inner divider on white surfaces — paler than --console-border. */
export const subtleBorderFromSidebar = (
  backgroundColor: string,
  neutralColor: string,
): string =>
  `color-mix(in lch increasing hue, ${backgroundColor} 10%, ${neutralColor} 100%)`;

/** Light surface tint on white chrome (pill, dropdown row hover, muted panels). */
const surfaceMutedOnWhite = (
  backgroundColor: string,
  neutralColor: string,
  brandPercent = 10,
): string =>
  `color-mix(in srgb, ${backgroundColor} ${brandPercent}%, ${neutralColor})`;

export const institutionThemeToCssVars = (
  props: ThemeColorProperties,
): Record<string, string> => {
  const surfaceMuted = surfaceMutedOnWhite(
    props.backgroundColor,
    props.neutralColor,
    10,
  );
  const surfaceTable = surfaceMutedOnWhite(
    props.backgroundColor,
    props.neutralColor,
    8,
  );

  return {
    "--console-foreground": CONTENT_FOREGROUND_FIXED,
    "--console-accent": props.secondaryColor,
    "--console-text-link": props.primaryColor,
    "--console-text-label": props.quaternaryColor,
    "--console-border": borderFromSidebar(
      props.backgroundColor,
      props.neutralColor,
      "default",
    ),
    "--console-border-input": borderFromSidebar(
      props.backgroundColor,
      props.neutralColor,
      "input",
    ),
    "--console-border-subtle": subtleBorderFromSidebar(
      props.backgroundColor,
      props.neutralColor,
    ),
    "--console-pill-bg": surfaceMutedOnWhite(
      props.backgroundColor,
      props.neutralColor,
      10,
    ),
    "--console-pill-bg-hover": surfaceMutedOnWhite(
      props.backgroundColor,
      props.neutralColor,
      18,
    ),
    "--console-pill-fg": props.primaryColor,
    "--console-surface-muted": surfaceMuted,
    "--console-surface-table-header": surfaceTable,
    "--console-surface-table-hover": surfaceTable,
    "--console-kpi-primary": props.primaryColor,
    "--console-kpi-primary-foreground": "#ffffff",
    "--console-sidebar-initials-fg": props.backgroundColor,
  };
};

export const defaultThemeCssVars = institutionThemeToCssVars(defaultTheme);
